import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Send, FileSignature } from 'lucide-react';
import {
  COUNTERPARTY_SUBJECTS as COUNTERPARTY_SUBJECTS_RAW,
  COUNTERPARTY_AGREEMENTS as COUNTERPARTY_AGREEMENTS_RAW,
  MERGE_FIELD_CATALOG as MERGE_FIELD_CATALOG_RAW,
} from '@platform/shared-types';
import type { CounterpartySubject, MergeFieldDef } from '@platform/shared-types';
import { StaffPage } from '../../components/staff/StaffChrome';
import LegalNav from './LegalNav';
import SearchableDropdown from '../../components/SearchableDropdown';
import { toastService } from '../../services/toastService';
import documentsApi from '../../services/documentsApi';
import { documentsExtApi } from '../../services/documentsExtApi';
import type { SendCounterpartyAgreementResult } from '../../services/documentsExtApi';

/**
 * SEND AN AGREEMENT — vendor, employee, partner or software.
 *
 * ONE SCREEN FOR FOUR SUBJECTS, mirroring the single handler behind it. The
 * form is not hand-written per subject: the counterparty's fields ARE the merge
 * catalog's fields for that subject's group, so adding `vendor_tax_id` to the
 * catalog puts an input on this page with no edit here at all. That is the same
 * property the palette and the publish gate rely on — one catalog, no copies.
 *
 * The `*_` prefix is stripped on the way in and re-applied by the service
 * through `composeMergeData`, because the prefix rule lives next to the catalog
 * that defines it and must not be restated in a form.
 */

/* Guarded runtime imports (project rule): a stale Vite pre-bundle resolves a
   new export to `undefined`, and mapping over that would render a blank page
   with no clue why. */
const STALE = (name: string) =>
  `@platform/shared-types exported no ${name} — a stale Vite pre-bundle. `
  + 'RESTART the dev server: deleting node_modules/.vite under a running server does nothing.';

const SUBJECTS: readonly CounterpartySubject[] = Array.isArray(COUNTERPARTY_SUBJECTS_RAW)
  ? COUNTERPARTY_SUBJECTS_RAW : [];
const AGREEMENTS: Record<string, any> = COUNTERPARTY_AGREEMENTS_RAW ?? {};
const CATALOG: readonly MergeFieldDef[] = Array.isArray(MERGE_FIELD_CATALOG_RAW)
  ? MERGE_FIELD_CATALOG_RAW : [];
if (!SUBJECTS.length || !CATALOG.length) {
  // eslint-disable-next-line no-console
  console.error(STALE('COUNTERPARTY_SUBJECTS / MERGE_FIELD_CATALOG'));
}

const INPUT =
  'w-full min-h-[42px] rounded-lg border border-stone-300 bg-white px-3 py-2 text-[13px] '
  + 'text-stone-900 placeholder:text-stone-400 focus:border-rose-400 focus:outline-none';

const LABEL = 'mb-1 block text-[11.5px] font-medium text-stone-600';

/** `vendor_legal_name` → `legalName`, the shape the provider takes. */
function toCamel(key: string): string {
  return key.replace(/_([a-z])/g, (_m, c) => c.toUpperCase());
}

interface PackageRow { name: string; monthlyPrice: string; features: string; limits: string }

const blankPackage = (): PackageRow => ({ name: '', monthlyPrice: '', features: '', limits: '' });

const SendAgreementPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [subject, setSubject] = useState<CounterpartySubject>(
    (params.get('subject') as CounterpartySubject) || 'vendor',
  );
  const [subjectId, setSubjectId] = useState(params.get('subjectId') || '');
  const [tenantId, setTenantId] = useState('platform');
  const [partyName, setPartyName] = useState('');
  const [partyEmail, setPartyEmail] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [packages, setPackages] = useState<PackageRow[]>([blankPackage()]);
  /** Empty = the company profile's default signatory (normally the CEO). */
  const [signatoryId, setSignatoryId] = useState<string | null>(null);
  const [signatories, setSignatories] = useState<
    { signatoryId: string; name: string; title: string; hasSignature: boolean }[]
  >([]);

  const [documents, setDocuments] = useState<{ documentId: string; title: string; contexts?: string[] }[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<SendCounterpartyAgreementResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const def = AGREEMENTS[subject] ?? {};

  /** The catalog's own fields for this subject — the form, derived not written. */
  const subjectFields = useMemo(
    () => CATALOG.filter((d) => d.group === def.fieldGroup),
    [def.fieldGroup],
  );

  const load = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const res = await documentsApi.listDocuments({ status: 'active' });
      setDocuments((res.documents || []).map((d: any) => ({
        documentId: d.documentId, title: d.title, contexts: d.contexts,
      })));
    } catch (e: any) {
      setError(e?.message || 'Could not load documents');
    } finally {
      setLoadingDocs(false);
    }
    // Who may sign for us. Best effort: an unreachable profile must not stop an
    // agreement being sent — it just means the default signatory is used and,
    // if none is configured, somebody countersigns by hand.
    try {
      const res: any = await documentsExtApi.getCompanyProfile();
      const profile = res?.profile;
      const list = Array.isArray(profile?.signatories) ? profile.signatories : [];
      setSignatories(list.map((row: any) => ({
        signatoryId: row.signatoryId, name: row.name, title: row.title, hasSignature: !!row.signature,
      })));
      setSignatoryId(profile?.defaultSignatoryId ?? null);
    } catch {
      setSignatories([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /**
   * Only documents that DECLARE this subject's context are offered. A document
   * declaring none is still offered — it kept the permissive publish default,
   * so nothing can say it is wrong for this subject.
   */
  const documentChoices = useMemo(() => documents
    .filter((d) => !d.contexts?.length || d.contexts.includes(def.mergeContext))
    .map((d) => ({
      value: d.documentId,
      label: d.title,
      subtitle: d.contexts?.length ? d.contexts.join(', ') : 'no context declared',
    })), [documents, def.mergeContext]);

  // Switching subject invalidates the document and the fields: the two field
  // groups do not overlap, and carrying a vendor's EIN onto an employment
  // agreement would silently send data about the wrong party.
  const changeSubject = (next: CounterpartySubject) => {
    setSubject(next);
    setFields({});
    setDocumentId(null);
    setResult(null);
    setTenantId(next === 'vendor' || next === 'partner' ? 'platform' : '');
  };

  const canSend = !!subjectId.trim() && !!tenantId.trim() && !!partyName.trim()
    && /.+@.+\..+/.test(partyEmail) && !!documentId
    && (subject !== 'software' || packages.some((p) => p.name.trim()));

  const send = async () => {
    if (!canSend || sending) return;
    setSending(true);
    setError(null);
    setResult(null);
    try {
      const res = await documentsExtApi.sendCounterpartyAgreement({
        subject,
        subjectId: subjectId.trim(),
        tenantId: tenantId.trim(),
        documentIds: [documentId!],
        companySignatoryId: signatoryId || undefined,
        party: { name: partyName.trim(), email: partyEmail.trim() },
        // Prefix stripped and camelised — the service re-applies it through the
        // catalog, so this form never restates the naming rule.
        fields: Object.fromEntries(
          Object.entries(fields)
            .filter(([, v]) => v.trim())
            .map(([k, v]) => [toCamel(k.replace(`${def.fieldGroup}_`, '')), v.trim()]),
        ),
        ...(subject === 'software' ? {
          packages: packages.filter((p) => p.name.trim()).map((p) => ({
            name: p.name.trim(),
            monthlyPriceCents: p.monthlyPrice.trim()
              ? Math.round(Number(p.monthlyPrice) * 100) : undefined,
            features: p.features.trim() ? p.features.split(',').map((f) => f.trim()).filter(Boolean) : undefined,
            limits: p.limits.trim() || undefined,
          })),
        } : {}),
      });
      setResult(res);
      if (res.notReady.length) {
        toastService.error(`${res.notReady.length} document(s) could not be sent — see below`);
      } else {
        toastService.success('Agreement sent for signature');
      }
    } catch (e: any) {
      setError(e?.message || 'send_failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <StaffPage section="Legal" tabs={<LegalNav />} icon={FileSignature} title="Send agreement" subtitle="Vendor, employee, partner or software">
      <div className="mx-auto max-w-3xl space-y-5">
        {error && (
          <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" />
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        {/* Subject */}
        <section className="rounded-xl border border-stone-200 bg-white p-4">
          <h3 className="mb-3 text-[12px] font-semibold uppercase tracking-wider text-stone-500">
            What kind of agreement
          </h3>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {SUBJECTS.map((s) => {
              const d = AGREEMENTS[s] ?? {};
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => changeSubject(s)}
                  className={`min-h-[44px] rounded-lg border px-3 py-2 text-[12.5px] font-medium ${
                    subject === s
                      ? 'border-rose-300 bg-rose-50 text-rose-700'
                      : 'border-stone-300 text-stone-600 hover:border-stone-400'
                  }`}
                >
                  {d.label ?? s}
                </button>
              );
            })}
          </div>
          {def.hint && <p className="mt-2.5 text-[11.5px] text-stone-500">{def.hint}</p>}
        </section>

        {/* Who and what */}
        <section className="rounded-xl border border-stone-200 bg-white p-4">
          <h3 className="mb-3 text-[12px] font-semibold uppercase tracking-wider text-stone-500">
            The agreement
          </h3>
          <div className="grid gap-3.5 md:grid-cols-2">
            <div>
              <label className={LABEL}>Document</label>
              <SearchableDropdown
                options={documentChoices}
                value={documentId}
                onChange={setDocumentId}
                placeholder={loadingDocs ? 'Loading…' : 'Choose the document to send'}
              />
              <p className="mt-1 text-[11px] text-stone-400">
                Only documents declaring the <b>{def.mergeContext}</b> context, plus any that
                declare none.
              </p>
            </div>
            <div>
              <label className={LABEL}>Reference id</label>
              <input
                className={INPUT}
                value={subjectId}
                onChange={(e) => setSubjectId(e.target.value)}
                placeholder={subject === 'employee' ? 'staff id' : `${subject} id`}
              />
              <p className="mt-1 text-[11px] text-stone-400">
                What this agreement is ABOUT — recorded on the request so it can be found again.
              </p>
            </div>
            <div>
              <label className={LABEL}>We sign as</label>
              <SearchableDropdown
                options={signatories.map((sg) => ({
                  value: sg.signatoryId,
                  label: `${sg.name} — ${sg.title}`,
                  subtitle: sg.hasSignature
                    ? 'signature stored — countersigns automatically'
                    : 'no signature — countersign by hand',
                }))}
                value={signatoryId}
                onChange={setSignatoryId}
                placeholder={signatories.length ? 'Default signatory' : 'None configured'}
              />
              <p className="mt-1 text-[11px] text-stone-400">
                Assign a sales rep to own this one, or leave the default. With their signature
                stored, this agreement executes itself the moment the other party signs.
              </p>
            </div>
            <div>
              <label className={LABEL}>Tenant</label>
              <input
                className={INPUT}
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                placeholder="platform"
              />
              <p className="mt-1 text-[11px] text-stone-400">
                {subject === 'vendor' || subject === 'partner'
                  ? 'Ours, not a tenant\'s — leave as platform.'
                  : 'The tenant this agreement belongs to.'}
              </p>
            </div>
          </div>

          <div className="mt-3.5 grid gap-3.5 md:grid-cols-2">
            <div>
              <label className={LABEL}>Signer name</label>
              <input className={INPUT} value={partyName} onChange={(e) => setPartyName(e.target.value)} />
            </div>
            <div>
              <label className={LABEL}>Signer email</label>
              <input
                className={INPUT}
                type="email"
                value={partyEmail}
                onChange={(e) => setPartyEmail(e.target.value)}
              />
              <p className="mt-1 text-[11px] text-stone-400">
                They sign through an emailed link and a one-time code — no login needed.
              </p>
            </div>
          </div>
        </section>

        {/* The counterparty's own details — the catalog IS the form */}
        {subject !== 'software' && (
          <section className="rounded-xl border border-stone-200 bg-white p-4">
            <h3 className="mb-1 text-[12px] font-semibold uppercase tracking-wider text-stone-500">
              {def.label} details
            </h3>
            <p className="mb-3 text-[11.5px] text-stone-500">
              Every field the merge catalog knows for this party. Leave one blank and any document
              using it will say so instead of rendering an empty line.
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              {subjectFields.map((f) => (
                <div key={f.name}>
                  <label className={LABEL}>{f.label}</label>
                  <input
                    className={INPUT}
                    value={fields[f.name] ?? ''}
                    onChange={(e) => setFields((p) => ({ ...p, [f.name]: e.target.value }))}
                    placeholder={f.example}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Software packages — also the row source for a package schedule */}
        {subject === 'software' && (
          <section className="rounded-xl border border-stone-200 bg-white p-4">
            <h3 className="mb-1 text-[12px] font-semibold uppercase tracking-wider text-stone-500">
              Packages being licensed
            </h3>
            <p className="mb-3 text-[11.5px] text-stone-500">
              These fill the <code>package_*</code> fields and every row of a package schedule.
              With more than one, the singular fields (<code>package_name</code>) deliberately stay
              empty — use <code>package_name_list</code> or a schedule.
            </p>
            <div className="space-y-2.5">
              {packages.map((p, i) => (
                <div key={i} className="grid gap-2 md:grid-cols-4">
                  <input
                    className={INPUT} placeholder="Package name" value={p.name}
                    onChange={(e) => setPackages((prev) => prev.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)))}
                  />
                  <input
                    className={INPUT} placeholder="Monthly $" value={p.monthlyPrice} inputMode="decimal"
                    onChange={(e) => setPackages((prev) => prev.map((r, j) => (j === i ? { ...r, monthlyPrice: e.target.value } : r)))}
                  />
                  <input
                    className={INPUT} placeholder="Features, comma separated" value={p.features}
                    onChange={(e) => setPackages((prev) => prev.map((r, j) => (j === i ? { ...r, features: e.target.value } : r)))}
                  />
                  <input
                    className={INPUT} placeholder="Limits" value={p.limits}
                    onChange={(e) => setPackages((prev) => prev.map((r, j) => (j === i ? { ...r, limits: e.target.value } : r)))}
                  />
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setPackages((p) => [...p, blankPackage()])}
              className="mt-2.5 min-h-[38px] rounded-lg border border-dashed border-stone-300 px-3 text-[12px] font-semibold text-stone-500 hover:border-rose-300 hover:text-rose-600"
            >
              + Add package
            </button>
          </section>
        )}

        {/* Result */}
        {result && (
          <section className="rounded-xl border border-stone-200 bg-white p-4">
            <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-wider text-stone-500">
              Result
            </h3>
            <p className="text-[12.5px] text-stone-600">
              Request <span className="font-mono">{result.requestId}</span>
            </p>
            {result.sent.map((s) => (
              <div key={s.itemId} className="mt-2 flex items-start gap-2 text-[12.5px] text-emerald-700">
                <CheckCircle2 size={15} className="mt-0.5 shrink-0" />
                <span>Sent · {s.documentId} {s.envelopeId ? `(envelope ${s.envelopeId})` : ''}</span>
              </div>
            ))}
            {result.notReady.map((n) => (
              <div key={n.itemId} className="mt-2 flex items-start gap-2 text-[12.5px] text-amber-700">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                <span>
                  Not sent · {n.documentId} — still needs:{' '}
                  <b>{n.missingFields.join(', ') || 'unknown fields'}</b>
                </span>
              </div>
            ))}
            <button
              type="button"
              onClick={() => navigate('/legal/signatures')}
              className="mt-3 min-h-[38px] rounded-lg border border-stone-300 px-3 text-[12px] font-semibold text-stone-600"
            >
              Go to signatures
            </button>
          </section>
        )}

        <div className="flex justify-end pb-8">
          <button
            type="button"
            disabled={!canSend || sending}
            onClick={send}
            className="flex min-h-[44px] items-center gap-2 rounded-lg px-5 text-[13px] font-semibold text-white disabled:opacity-50"
            style={{ background: '#DC143C' }}
          >
            {sending ? <RubyGemMark size={16} drawing color="currentColor" /> : <Send size={16} />}
            {sending ? 'Sending…' : 'Send for signature'}
          </button>
        </div>
      </div>
    </StaffPage>
  );
};

export default SendAgreementPage;
